export default class SessionManager {
  constructor(
    extendSessionMethod,
    setSessionStartTime,
    getSessionStartTime,
    activityThresholdTime,
    warningPaddingTime,
    initiateExtensionPadding,
    keepSessionAliveInterval,
    isSessionAlivePadding,
  ) {
    this.extendSessionMethod = extendSessionMethod;
    this.getSessionStartTime = getSessionStartTime;
    this.setSessionStartTime = setSessionStartTime;
    this.activityThresholdTime = activityThresholdTime;
    this.warningPaddingTime = warningPaddingTime;
    this.initiateExtensionPadding = initiateExtensionPadding;
    this.keepSessionAliveInterval = keepSessionAliveInterval;
    this.isSessionAlivePadding = isSessionAlivePadding;
  }

  isSessionAlive = () =>
    new Date() - new Date(this.getSessionStartTime()) <=
    this.keepSessionAliveInterval + this.isSessionAlivePadding;

  /*
	Keeps backend TR session alive for longer (20 extra minutes?)
	Sets Session Start Time to current time
	*/
  extendSession(swallowError = false) {
    const extendResponse = this.extendSessionMethod();
    if (extendResponse) {
      this.setSessionStartTime(new Date());
    } else if (this.handleErrorMethod && !swallowError) {
      this.handleErrorMethod('attempt to extend session failed');
    }
    return extendResponse;
  }

  // calls setLastActivityTime
  // if time since Session Start Time in greater than INITIATE_EXTENSION_PADDING, call extendSession.
  // setup timer to call checkActivity in ACTIVITY_THRESHOLD - WARNING_PADDING seconds
  initiateTimer() {
    this.setLastActivityTime();

    const sessionStartTime = this.getSessionStartTime();
    if (!!sessionStartTime) {
      const timeSinceSessionStart = new Date() - new Date(sessionStartTime);
      if (timeSinceSessionStart > this.initiateExtensionPadding) {
        this.extendSession();
      }
    }

    if (!!this.timeoutActivity) {
      clearTimeout(this.timeoutActivity);
    }
    if (!!this.timeoutSessionKeepAlive) {
      clearTimeout(this.timeoutSessionKeepAlive);
    }

    this.timeoutSessionKeepAlive = setTimeout(() => {
      this.keepSessionAlive();
    }, this.keepSessionAliveInterval);

    this.timeoutActivity = setTimeout(() => {
      this.checkActivity();
    }, this.activityThresholdTime - this.warningPaddingTime);
  }

  // sets Last Activity Time to the current time
  setLastActivityTime() {
    this.lastActivity = new Date();
  }

  keepSessionAlive() {
    if (this.isSessionAlive()) {
      this.extendSession();

      this.timeoutSessionKeepAlive = setTimeout(() => {
        this.keepSessionAlive();
      }, this.keepSessionAliveInterval);
    } // else if session is already dead, don't bother trying to keep it alive
  }

  // if time since Last Activity Time is greater than ACTIVITY_THRESHOLD - WARNING_PADDING seconds, call warningMethod
  // call extendSession
  // call checkActivity in (ACTIVITY_THRESHOLD - WARNING_PADDING) - time since Last Activity Time
  checkActivity() {
    const timeSinceLastActivity = new Date() - this.lastActivity;
    const timeTillWarning = this.activityThresholdTime - this.warningPaddingTime;

    if (!this.isSessionAlive()) {
      /* if the session hasn't been refreshed for longer than this.keepSessionAliveInterval,
         consider it dead, regardless of timeSinceLastActivity */
      this.logoutMethod(true);
      return;
    } else if (timeSinceLastActivity >= timeTillWarning) {
      if (!!this.timeoutSessionKeepAlive) {
        if (!!this.warningMethod) {
          this.warningMethod();
        }

        clearTimeout(this.timeoutSessionKeepAlive);
      }
      return;
    }
    setTimeout(() => {
      this.checkActivity();
    }, timeTillWarning - timeSinceLastActivity);
  }

  setHandleErrorMethod(handleError) {
    this.handleErrorMethod = handleError;
  }

  setWarningMethod(warningMethod) {
    this.warningMethod = warningMethod;
  }

  setLogoutMethod(logoutMethod) {
    this.logoutMethod = logoutMethod;
  }

  logout() {
    if (this.logoutMethod && typeof this.logoutMethod === 'function') {
      this.logoutMethod();
    }

    this.unloadSessionManager();
  }

  unloadSessionManager() {
    if (this.timeoutActivity) {
      clearTimeout(this.timeoutActivity);
    }
    if (this.timeoutSessionKeepAlive) {
      clearTimeout(this.timeoutSessionKeepAlive);
    }

    this.setWarningMethod(undefined);
    this.setLogoutMethod(undefined);
    this.setHandleErrorMethod(undefined);
  }
}
